"use client";
import { trackAccountEvent } from "@/app/directories/account/_analytics/account-analytics";
import { languageOptionsWithLocalizedLabels } from "@/app/directories/companion/components_v2/PatientHeader/lib/PatientHeaderHelper";
import { setUserLocale } from "@/i18n/locales";
import type {
  LanguageCode,
  UpdateUserInput,
} from "@/lib/graphql/generated-gql";
import { LoadingCirclesHoriz } from "@frontend/components/elements/LoadingCirclesHoriz";
import { updateUserAndEhrPatient } from "@frontend/lib/actions/actions";
import {
  Select,
  SelectContent,
  SelectItemRightCheck,
  SelectTrigger,
  SelectValue,
} from "@frontend/shared/components/ui/select";
import { cn } from "@frontend/shared/lib/utils";
import { useLocale } from "next-intl";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

const LanguageSelector = ({
  userId,
  primaryLanguage,
  className,
  contentClassName,
  itemClassName,
  orgSupportedLanguages,
}: {
  userId?: string;
  primaryLanguage: LanguageCode;
  className?: string;
  contentClassName?: string;
  itemClassName?: string;
  orgSupportedLanguages: LanguageCode[];
}) => {
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Set user locale to match primaryLanguage on first render only

  // next-intl's locale reflects the cookie value (set before reload),
  // while primaryLanguage comes from the DB (which may lag behind due to
  // the fire-and-forget EHR sync). Prefer the cookie-derived locale.
  const intlLocale = useLocale().replace("-", "_") as LanguageCode;

  const supportedLanguageOptionsWithLocalizedLabels =
    languageOptionsWithLocalizedLabels.filter((option) =>
      orgSupportedLanguages?.includes(option.value as LanguageCode),
    );

  const defaultLanguage = orgSupportedLanguages?.includes(intlLocale)
    ? intlLocale
    : orgSupportedLanguages?.includes(primaryLanguage)
      ? primaryLanguage
      : ("en" as LanguageCode.En);

  // Use local state to track the selected language
  const [selectedLanguage, setSelectedLanguage] =
    useState<LanguageCode>(defaultLanguage);

  // Sync local state with prop changes
  useEffect(() => {
    setSelectedLanguage(defaultLanguage);
  }, [defaultLanguage]);

  return loading ? (
    <div
      className={cn(
        "flex justify-center items-center bg-white  border border-[#E1E1E1] py-4 gap-2 rounded-full h-10 sm:w-full md:max-w-40 ",
        className,
      )}
    >
      <LoadingCirclesHoriz />
    </div>
  ) : userId ? (
    <Select
      value={selectedLanguage}
      onValueChange={async (value) => {
        if (value === selectedLanguage) {
          return;
        }
        trackAccountEvent("nav:language_changed", {
          from: selectedLanguage,
          to: value,
        });
        setSelectedLanguage(value as LanguageCode);
        const updateInput: UpdateUserInput = {
          id: userId,
          user: {
            primaryLanguage: { set: value.replace("-", "_") as LanguageCode },
          },
        };
        // Set cookie, then hard reload for new locale.
        // The backend mutation (updateUserAndEhrPatient) is fire-and-forget:
        // it runs server-side via a Next.js server action, so the server
        // continues processing even after the client reloads.
        await setUserLocale(value as LanguageCode);
        updateUserAndEhrPatient(updateInput).catch((error) => {
          console.warn(
            "LanguageSelector: Error updating user language:",
            userId,
            error,
          );
        });
        window.location.reload();
      }}
    >
      <SelectTrigger className={cn("rounded-full w-full", className)}>
        <SelectValue placeholder="Select a language" />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {supportedLanguageOptionsWithLocalizedLabels.map((option) => (
          <SelectItemRightCheck
            key={option.value}
            value={option.value}
            className={itemClassName}
          >
            <span className="font-light text-sm">
              {option.label.slice(0, -5)}
            </span>
            <span className="text-sm pl-1 font-light">
              {option.label.slice(-5)}
            </span>
          </SelectItemRightCheck>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <Select
      value={selectedLanguage}
      onValueChange={async (value) => {
        if (value === selectedLanguage) {
          return;
        }
        trackAccountEvent("nav:language_changed", {
          from: selectedLanguage,
          to: value,
        });
        setSelectedLanguage(value as LanguageCode);
        setLoading(true);
        await setUserLocale(value as LanguageCode);
        router.refresh();
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }}
    >
      <SelectTrigger className={cn("rounded-full w-full", className)}>
        <SelectValue placeholder="Select a language" />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        {supportedLanguageOptionsWithLocalizedLabels.map((option) => (
          <SelectItemRightCheck
            key={option.value}
            value={option.value}
            className={itemClassName}
          >
            <span className="font-light text-sm">
              {option.label.slice(0, -5)}
            </span>
            <span className="text-sm pl-1 font-light">
              {option.label.slice(-5)}
            </span>
          </SelectItemRightCheck>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSelector;
